/**
 * The pricing page's hire card, as behaviour: the term and team-size picker, seeding it from a live plan, and changing
 * that plan (preview, then apply). ONE copy, read by today's /pricing (src/pages/Pricing.tsx) and its rebuild in the new
 * design, so the two cannot answer the same click differently (design-system §7b: behaviour is lifted into a hook,
 * never copied).
 *
 * - Signed out, or on a workspace with no plan, the picker is the offer: it starts at one Prest0n on the annual hire.
 * - A live plan SEEDS the picker once (its term and team size), which turns it into the adjuster, the same seeding rule
 *   the subscription page's adjuster uses. After that the person's choice is theirs.
 * - A change is previewed on the server (debounced 500 ms) and applied there; both go through ONE reader of the
 *   server's answer (src/services/planChangeClient.ts). A change the card on file cannot carry comes back as
 *   `needsCheckout` and is handed to the caller (`onNeedsCheckout`), which sends the person where the card machinery
 *   lives. Before 2026-09-30 that answer was read only on a 2xx and so never arrived.
 *
 * The words it produces (the preview and applied lines) are the page's; they live here so both pages say them alike.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { HireCadence } from '../config/workUnits';
import { invalidateBillingSnapshot } from '../services/billingSnapshotCache';
import { requestPlanChange, type PlanChangeAnswer, type PlanChangeDeps } from '../services/planChangeClient';

/** What the card needs to know about the workspace (a WorkspaceBillingSnapshot satisfies it). */
export interface HirePickerWorkspace {
  kind: string;
  accountId?: string;
  term?: HireCadence;
  seats?: number;
  canManage?: boolean;
  entitlementSource?: string | null;
}

export type PlanChangeState =
  | { phase: 'idle' }
  | { phase: 'preview'; line: string }
  | { phase: 'applying' }
  | { phase: 'applied'; line: string }
  | { phase: 'failed' };

export interface HirePickerOptions {
  /** The signed-in person's id: their cached billing answer is dropped the moment a change goes out. */
  uid?: string | null;
  /** The card on file cannot carry this change: send the person to the page that can take a card. */
  onNeedsCheckout: (accountId: string | undefined) => void;
  /** Tests only: the network. */
  deps?: PlanChangeDeps;
}

export const PLAN_CHANGE_PREVIEW_DEBOUNCE_MS = 500;

/** "$3,200": whole-dollar display. */
const fmtUsd = (n: number): string => `$${n.toLocaleString('en-US')}`;

/** Where "full billing & invoices" (and a change that needs a card) goes for this workspace. */
export function subscriptionPathFor(accountId: string | undefined): string {
  return accountId ? `/subscription?account=${accountId}` : '/subscription';
}

/** The preview line for an answer, or null when there is nothing to say. */
export function previewLine(answer: PlanChangeAnswer): string | null {
  if (answer.kind !== 'ok') return null;
  return answer.effectiveAt === 'now'
    ? `applies immediately${typeof answer.amountDueNowCents === 'number' && answer.amountDueNowCents > 0 ? ` — about ${fmtUsd(answer.amountDueNowCents / 100)} prorated to your card on file` : ''}`
    : 'nothing charged today — this takes effect when your commitment is up';
}

export function useHirePicker(ws: HirePickerWorkspace, options: HirePickerOptions) {
  const { uid, onNeedsCheckout, deps } = options;
  const [cadence, setCadence] = useState<HireCadence>('annual');
  const [seats, setSeats] = useState(1);

  // A live plan seeds the picker ONCE, turning it into the adjuster.
  const seededRef = useRef(false);
  useEffect(() => {
    if (ws.kind !== 'plan' || seededRef.current) return;
    seededRef.current = true;
    if (ws.term) setCadence(ws.term);
    if (ws.seats) setSeats(ws.seats);
  }, [ws]);

  const planDirty = ws.kind === 'plan' && (cadence !== ws.term || seats !== ws.seats);
  // A plan PROVIDED by an internal paid grant (2026-09-15): a paid posture with no card on file and no Stripe
  // subscription behind it, so there is nothing to apply.
  const planIsGranted = ws.kind === 'plan' && ws.entitlementSource === 'internal-grant';

  const [change, setChange] = useState<PlanChangeState>({ phase: 'idle' });

  // Debounced preview. It is advisory: a failed preview says nothing, and apply still confirms on the server.
  useEffect(() => {
    // Back to idle only when not idle already: a caller that hands a fresh workspace object each render must not be
    // answered with a fresh state each time (the two would re-render each other without end).
    if (ws.kind !== 'plan' || !ws.canManage || !planDirty) {
      setChange((c) => (c.phase === 'idle' ? c : { phase: 'idle' }));
      return undefined;
    }
    let canceled = false;
    const t = setTimeout(async () => {
      const answer = await requestPlanChange(
        { mode: 'preview', term: cadence, seats, accountId: ws.accountId },
        deps,
      );
      if (canceled) return;
      const line = previewLine(answer);
      if (line) setChange({ phase: 'preview', line });
    }, PLAN_CHANGE_PREVIEW_DEBOUNCE_MS);
    return () => { canceled = true; clearTimeout(t); };
  }, [ws, planDirty, cadence, seats, deps]);

  const onNeedsCheckoutRef = useRef(onNeedsCheckout);
  onNeedsCheckoutRef.current = onNeedsCheckout;

  const applyChange = useCallback(async () => {
    if (ws.kind !== 'plan' || !ws.canManage || !planDirty) return;
    setChange({ phase: 'applying' });
    // The cached seats/term stop describing this workspace the moment the apply goes out: drop it so /subscription
    // cannot paint the old plan.
    invalidateBillingSnapshot(uid);
    const answer = await requestPlanChange(
      { mode: 'apply', term: cadence, seats, accountId: ws.accountId },
      deps,
    );
    if (answer.kind === 'needsCheckout') {
      // A change that needs a fresh checkout runs on the subscription page, where the full card machinery lives.
      setChange({ phase: 'idle' });
      onNeedsCheckoutRef.current(ws.accountId);
      return;
    }
    if (answer.kind !== 'ok') {
      setChange({ phase: 'failed' });
      return;
    }
    setChange({
      phase: 'applied',
      line: answer.effectiveAt === 'now'
        ? 'done — your change is live'
        : 'done — scheduled for the end of your current term',
    });
  }, [ws, planDirty, cadence, seats, uid, deps]);

  const seatsDown = useCallback(() => setSeats((s) => Math.max(1, s - 1)), []);
  const seatsUp = useCallback(() => setSeats((s) => s + 1), []);
  /** A typed team size: taken while it reads as a number, left alone otherwise. */
  const seatsTyped = useCallback((raw: string) => {
    const v = parseInt(raw, 10);
    if (Number.isFinite(v)) setSeats(v);
  }, []);
  /** Leaving the field settles it on at least one. */
  const seatsSettled = useCallback(() => setSeats((s) => Math.max(1, s || 1)), []);

  return {
    cadence,
    setCadence,
    seats,
    setSeats,
    seatsDown,
    seatsUp,
    seatsTyped,
    seatsSettled,
    planDirty,
    planIsGranted,
    change,
    applyChange,
  };
}
